import React from 'react';
import { Typography, Stack, Chip, Box, useTheme } from '@mui/material';
import { TemplateCategory } from '@/types/templateCategoryTypes'; // Adjust path
import { UiTemplate } from '@/pages/TemplateEditPage/templateEditTypes';

interface TemplateSummaryCardProps {
  template: UiTemplate;
  templateCategories: TemplateCategory[]; // To find category name if not populated in template.category
  saveStatus: 'idle' | 'success' | 'error' | 'saving';
  hasChanges: boolean;
  onSave: () => void; // General save for the button
}

const TemplateSummaryCard: React.FC<TemplateSummaryCardProps> = ({
  template,
  templateCategories
}) => {
  const theme = useTheme();
  const categoryName =
    template.category?.name ||
    templateCategories.find((cat) => cat.id === template.templateCategoryId)
      ?.name ||
    (template.templateCategoryId
      ? `ID: ${template.templateCategoryId}`
      : 'None');

  return (
    <Box
      sx={{
        p: 3,
        backgroundColor: theme.palette.background.paper,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px'
      }}
    >
      <Stack spacing={2.5}>
        <Box>
          <Typography
            sx={{
              lineHeight: 1.2,
              textAlign: 'left',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.8px',
              fontSize: '11px',
              mb: 0.75,
              color: 'text.secondary'
            }}
          >
            Name
          </Typography>
          <Typography
            sx={{
              wordBreak: 'break-word',
              textAlign: 'left',
              fontWeight: 600,
              fontSize: '16px',
              color: 'text.primary',
              lineHeight: 1.4
            }}
          >
            {template.name || '-'}
          </Typography>
        </Box>
        <Box>
          <Typography
            sx={{
              lineHeight: 1.2,
              textAlign: 'left',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.8px',
              fontSize: '11px',
              mb: 0.75,
              color: 'text.secondary'
            }}
          >
            Description
          </Typography>
          <Typography
            sx={{
              wordBreak: 'break-word',
              textAlign: 'left',
              fontSize: '13px',
              color: 'text.secondary',
              lineHeight: 1.5
            }}
          >
            {template.description || '-'}
          </Typography>
        </Box>
        <Box>
          <Typography
            sx={{
              lineHeight: 1.2,
              textAlign: 'left',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.8px',
              fontSize: '11px',
              mb: 1,
              color: 'text.secondary'
            }}
          >
            Category
          </Typography>
          <Box sx={{ textAlign: 'left' }}>
            <Chip
              label={categoryName}
              size="small"
              sx={{
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f3f4f6',
                color: theme.palette.text.primary,
                border: '1px solid',
                borderColor: theme.palette.divider,
                fontWeight: 500,
                fontSize: '12px',
                height: '24px',
                borderRadius: '6px'
              }}
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 3
          }}
        >
          <Box>
            <Typography
              sx={{
                lineHeight: 1.2,
                textAlign: 'left',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
                fontSize: '11px',
                mb: 0.5,
                color: 'text.secondary'
              }}
            >
              Questions
            </Typography>
            <Typography
              sx={{
                textAlign: 'left',
                fontSize: '14px',
                color: 'text.primary',
                fontWeight: 600
              }}
            >
              {template.questions.length}
            </Typography>
          </Box>
          <Box>
            <Typography
              sx={{
                lineHeight: 1.2,
                textAlign: 'left',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
                fontSize: '11px',
                mb: 0.5,
                color: 'text.secondary'
              }}
            >
              Summaries
            </Typography>
            <Typography
              sx={{
                textAlign: 'left',
                fontSize: '14px',
                color: 'text.primary',
                fontWeight: 600
              }}
            >
              {template.summaries?.length || 0}
            </Typography>
          </Box>
        </Box>
        <Box>
          <Typography
            sx={{
              lineHeight: 1.2,
              textAlign: 'left',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.8px',
              fontSize: '11px',
              mb: 0.5,
              color: 'text.secondary'
            }}
          >
            Approval
          </Typography>
          <Typography
            sx={{
              fontSize: '13px',
              color: 'text.primary',
              fontWeight: 500,
              textAlign: 'left'
            }}
          >
            {template.requiresApproval ? 'Required' : 'Not Required'}
          </Typography>
        </Box>
        <Box>
          <Typography
            sx={{
              lineHeight: 1.2,
              textAlign: 'left',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.8px',
              fontSize: '11px',
              mb: 0.75,
              color: 'text.secondary'
            }}
          >
            AI Title Generation
          </Typography>
          <Typography
            sx={{
              textAlign: 'left',
              fontSize: '13px',
              color: 'text.secondary',
              lineHeight: 1.5,
              wordBreak: 'break-word'
            }}
          >
            {template.titleGenerationPrompt || 'Using default prompt'}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 3
          }}
        >
          <Box>
            <Typography
              sx={{
                lineHeight: 1.2,
                textAlign: 'left',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
                fontSize: '11px',
                mb: 0.5,
                color: 'text.secondary'
              }}
            >
              Created
            </Typography>
            <Typography
              sx={{
                textAlign: 'left',
                fontSize: '12px',
                color: 'text.secondary',
                fontWeight: 400
              }}
            >
              {new Date(template.createdAtUtc).toLocaleDateString()}
            </Typography>
          </Box>
          <Box>
            <Typography
              sx={{
                lineHeight: 1.2,
                textAlign: 'left',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
                fontSize: '11px',
                mb: 0.5,
                color: 'text.secondary'
              }}
            >
              Last Modified
            </Typography>
            <Typography
              sx={{
                textAlign: 'left',
                fontSize: '12px',
                color: 'text.secondary',
                fontWeight: 400
              }}
            >
              {new Date(template.updatedAtUtc).toLocaleDateString()}
            </Typography>
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};

export default TemplateSummaryCard;
